// ------------------------------------------------------------------------------
// name: useSafeQuery
// author: mudas
// created: 2025/8/19
// ------------------------------------------------------------------------------

import { useComputed } from 'common/hooks';
import { hashQueryKeyByOptions } from './utils';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { QueryKey } from '@tanstack/query-core';

import { DebounceQueryOptions, useDebounceQuery } from './plugins/useDebounceQuery';
import { ThrottleQueryOptions, useThrottleQuery } from './plugins/useThrottleQuery';
import { FetchingDelayOptions, useFetchingDelay } from './plugins/useFetchingDelay';

/**
 * 支持 isFetching 延迟生效和防抖查询的 useQuery
 * @param options
 */
export const useSafeQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
    DebounceQueryOptions &
    ThrottleQueryOptions &
    FetchingDelayOptions,
) => {
  const {
    queryKey,
    queryHash,
    queryKeyHashFn,

    fetchingDelay,

    debounceWait,
    debounceLeading,
    debounceTrailing,
    debounceMaxWait,

    throttleWait,
    throttleLeading,
    throttleTrailing,

    ...queryOptions
  } = options;

  const queryKeyHash = useComputed<string>(() => {
    return hashQueryKeyByOptions({ queryKey, queryHash, queryKeyHashFn });
  }, [queryKey, queryHash, queryKeyHashFn]);

  // --------------------------------------------------------------------------
  //
  // 针对 options 查询参数的修改插件
  //
  // --------------------------------------------------------------------------

  const [debounceQueryOptions] = useDebounceQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    debounceWait,
    debounceLeading,
    debounceTrailing,
    debounceMaxWait,
  });

  const [throttleQueryOptions] = useThrottleQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    throttleWait,
    throttleLeading,
    throttleTrailing,
  });

  // --------------------------------------------------------------------------
  //
  // 针对 response 查询结果的修改插件
  //
  // --------------------------------------------------------------------------

  const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    // networkMode 默认 'online' 时，onlineManager 判定离线会让 query 静默停在
    // fetchStatus: 'paused' —— 不进 retryer、不发请求、不报错，组件只会拿到空数据；
    // 而命中缓存的地方照常渲染旧值，整个页面看起来一切正常，排查极其隐蔽。
    // 而 onlineManager 回退到 navigator.onLine，Safari 下会误报 false（睡醒、切网后
    // 尤其常见），一误报就是全站 query 一起悄悄停摆。
    // 这里固定 'always'，保证请求一定发出：真离线时只是快速失败进入 error 态（可见、
    // 可重试），而不是无声无息地卡住；恢复联网仍由 refetchOnReconnect 兜底。
    // 确实需要旧行为时，调用方仍可显式传入 networkMode 覆盖（见下方展开顺序）。
    networkMode: 'always',

    queryKey, // 虽然不影响查询的触发，但在 invalidateQueries 等操作定位为目标时需要
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    ...queryOptions,
    ...debounceQueryOptions,
    ...throttleQueryOptions,
  });

  const [isFetching] = useFetchingDelay({
    fetchStatus: query.fetchStatus,
    fetchingDelay,
  });

  return {
    ...query,
    isFetching,
  };
};
